
import React, { useState, useMemo } from 'react';
import { usePromotionProducts } from '../../cart/hooks/usePromotionProducts';
import { useCartContext } from '../../cart';

/**
 * Shows "Ofertas combinables" for PRODUCT-scoped cart promotions.
 * Lets the user pick quantities across multiple products to reach a discount threshold.
 */
export const OfertasCombinables = ({ promotions, catalogProducts = [], sellingPricePerUnit, onActiveItemsChange }) => {
    const productPromos = useMemo(() =>
        (promotions ?? [])
            .filter(p => p.scope === 'PRODUCT' && p.discountPercentage
                && (p.stackingType || p.stacking_type) !== 'OVERRIDE_FULL')
            .sort((a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0)),
        [promotions]
    );

    const leadPromo = productPromos[0] ?? null;
    const { products: groupProducts } = usePromotionProducts(leadPromo?.id ?? null);

    const [quantities, setQuantities] = useState({});
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [applied, setApplied] = useState(false);

    const totalQty = Object.values(quantities).reduce((sum, q) => sum + (q || 0), 0);

    const activeTiers = useMemo(() =>
        productPromos.filter(p => totalQty >= (p.minQuantity ?? 0)),
        [productPromos, totalQty]
    );

    const effectiveTiers = useMemo(() => {
        // Sort by priority descending strictly like backend
        const sorted = [...activeTiers].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

        const groupWinners = new Set();
        const result = [];

        for (const tier of sorted) {
            const extGroup = tier.exclusiveGroup || tier.exclusive_group;
            if (extGroup) {
                if (!groupWinners.has(extGroup)) {
                    groupWinners.add(extGroup);
                    result.push(tier);
                }
            } else {
                result.push(tier);
            }
        }
        return result;
    }, [activeTiers]);

    // Sort active tiers so they apply in the same priority order as the backend if needed,
    // although here we are mostly summing matching tiers for the same product.
    const { activeDiscountPercentageResult, overrideFullFixedPrice } = useMemo(() => {
        let currentPriceFactor = 1.0;
        let parallelDiscountSum = 0;
        let overrideFullPrice = null;

        effectiveTiers.forEach(tier => {
            const stacking = tier.stackingType || tier.stacking_type || 'PARALLEL';
            const disc = tier.discountPercentage ?? 0;

            if (stacking === 'OVERRIDE_FULL' && tier.fixedPrice != null && sellingPricePerUnit > 0) {
                overrideFullPrice = tier.fixedPrice;
                parallelDiscountSum = 0;
                currentPriceFactor = 1.0;
            } else if (stacking === 'CASCADE') {
                currentPriceFactor = currentPriceFactor * (1 - (disc / 100.0));
            } else if (stacking === 'PARALLEL') {
                parallelDiscountSum += disc;
            } else if (stacking === 'OVERRIDE') {
                parallelDiscountSum = disc;
                currentPriceFactor = 1.0;
            } else if (stacking === 'OVERRIDE_NEGOTIATED') {
                // Acts like a CASCADE base modifier: reduces currentPriceFactor
                // so subsequent PARALLEL tiers apply on the already-reduced price.
                // E.g. OVERRIDE_NEGOTIATED 10% → factor=0.90, then PARALLEL 50%:
                //   finalFactor = 0.90 × (1-0.50) = 0.45 → 55% effective discount ✓
                currentPriceFactor = currentPriceFactor * (1 - (disc / 100.0));
            }


        });

        if (overrideFullPrice !== null) {
            return {
                activeDiscountPercentageResult: Math.max(0, (1.0 - overrideFullPrice / sellingPricePerUnit) * 100.0),
                overrideFullFixedPrice: overrideFullPrice
            };
        }

        if (parallelDiscountSum > 0) {
            currentPriceFactor = currentPriceFactor * (1 - (parallelDiscountSum / 100.0));
        }

        return {
            activeDiscountPercentageResult: (1.0 - currentPriceFactor) * 100.0,
            overrideFullFixedPrice: null
        };
    }, [activeTiers, sellingPricePerUnit]);

    const nextTier = productPromos.find(p => totalQty < (p.minQuantity ?? 0)) ?? null;
    const missingForNext = nextTier ? (nextTier.minQuantity - totalQty) : 0;

    const { addProduct } = useCartContext();

    const handleQtyChange = (codigo, delta) => {
        setQuantities(prev => {
            const groupProduct = groupProducts.find(p => p.codigoArticulo === codigo);
            const stock = groupProduct?.stock;
            const currentQty = prev[codigo] ?? 0;
            const newQty = Math.max(0, currentQty + delta);

            if (stock != null && newQty > stock) {
                return prev;
            }

            return {
                ...prev,
                [codigo]: newQty
            };
        });
        setApplied(false);
        setApplyError(null);
    };

    const getProductPrice = React.useCallback((groupProduct) => {
        const catalogProduct = catalogProducts.find(cp =>
            String(cp.id) === String(groupProduct.id)
        );
        const isMainProduct = catalogProduct && (catalogProduct.code === groupProduct.codigoArticulo || catalogProduct.codigoArticulo === groupProduct.codigoArticulo);

        if (isMainProduct && sellingPricePerUnit != null) {
            return sellingPricePerUnit;
        } else {
            return catalogProduct?.price?.sellingPrice
                ?? catalogProduct?.price?.basePrice
                ?? groupProduct.precioVenta
                ?? groupProduct.precioBase
                ?? 0;
        }
    }, [catalogProducts, sellingPricePerUnit]);

    const getProductOriginalPrice = React.useCallback((groupProduct) => {
        const catalogProduct = catalogProducts.find(cp =>
            String(cp.id) === String(groupProduct.id)
        );
        return catalogProduct?.price?.value
            ?? catalogProduct?.price?.basePrice
            ?? groupProduct.precioBase
            ?? getProductPrice(groupProduct);
    }, [catalogProducts, getProductPrice]);

    const handleApply = React.useCallback(async () => {
        const entries = Object.entries(quantities).filter(([, qty]) => qty > 0);
        if (entries.length === 0) return;

        setApplying(true);
        setApplyError(null);
        setApplied(false);

        try {
            for (const [codigo, qty] of entries) {
                const groupProduct = groupProducts.find(p => p.codigoArticulo === codigo);
                if (!groupProduct) continue;

                let precio = getProductPrice(groupProduct);

                if (precio === 0) {
                    console.warn('[OfertasCombinables] No price found for', codigo, '— skipping');
                    continue;
                }

                const originalPrecio = getProductOriginalPrice(groupProduct) || precio;

                const catalogProduct = catalogProducts.find(cp => String(cp.id) === String(groupProduct.id));
                const realBcDiscounts = (catalogProduct && catalogProduct.price && catalogProduct.price.bcDiscounts)
                    ? catalogProduct.price.bcDiscounts
                    : {
                        bcDisc1: 0, bcDisc2: 0, bcDisc3: 0, bcDisc4: 0, bcDisc5: 0,
                        bcDisc6: 0, bcDisc7: 0, bcDisc8: 0, bcDisc9: 0, bcDisc10: 0
                    };

                await addProduct({
                    id: groupProduct.id,
                    quantity: qty,
                    price: { sellingPrice: precio, basePrice: originalPrecio },
                    bcDiscounts: realBcDiscounts
                });
            }
            setApplied(true);
            setQuantities({});
        } catch (err) {
            console.error('[OfertasCombinables] Error adding products to cart:', err);
            setApplyError(err?.message || 'Error desconocido al añadir productos');
        } finally {
            setApplying(false);
        }
    }, [quantities, groupProducts, getProductPrice, addProduct]);

    // Detect if the promo type is OVERRIDE (bypasses ERP discounts, applies % on catalog price).
    // Use productPromos (not activeTiers) because OVERRIDE is a property of the promo type,
    // not of whether the quantity threshold has been reached.
    const isOverrideStacking = useMemo(() =>
        productPromos.some(t => (t.stackingType || t.stacking_type) === 'OVERRIDE'),
        [productPromos]
    );
    // OVERRIDE_NEGOTIATED: visually uses basePrice (precioCompra) like PARALLEL,
    // because the backend already computes the correct price via d1-4 negotiatedPrice.
    // No frontend flag needed — falls through to default basePrice path.

    const { totalRawPrice, totalFinalPrice, totalSavings, exactDiscountPercentage } = useMemo(() => {
        let raw = 0;
        let final = 0;
        const discount = activeDiscountPercentageResult;

        Object.entries(quantities).forEach(([codigo, qty]) => {
            if (qty > 0) {
                const groupProduct = groupProducts.find(p => p.codigoArticulo === codigo);
                if (groupProduct) {
                    const price = getProductPrice(groupProduct);
                    const originalPrice = getProductOriginalPrice(groupProduct) || price;
                    raw += (originalPrice * qty);
                    // For OVERRIDE_FULL use fixed price; for OVERRIDE use catalog price as base;
                    // for others apply % to ERP selling price
                    let unitFinalPrice;
                    if (overrideFullFixedPrice != null) {
                        unitFinalPrice = overrideFullFixedPrice;
                    } else if (isOverrideStacking) {
                        unitFinalPrice = originalPrice * (1 - (discount / 100)); // catalog × promo%
                    } else {
                        unitFinalPrice = price * (1 - (discount / 100)); // ERP × promo%
                    }
                    final += (unitFinalPrice * qty);
                }
            }
        });

        // The exact accumulated percentage
        const exactDiscountPercentage = raw > 0 ? ((raw - final) / raw) * 100 : 0;

        return {
            totalRawPrice: raw,
            totalFinalPrice: final,
            totalSavings: raw - final,
            exactDiscountPercentage
        };
    }, [quantities, groupProducts, activeDiscountPercentageResult, getProductPrice, isOverrideStacking]);

    React.useEffect(() => {
        if (onActiveItemsChange) {
            const isActive = productPromos.length > 0 && groupProducts.length > 0 && totalQty > 0;
            onActiveItemsChange(isActive ? {
                isActive: true,
                totalQty,
                totalRawPrice,
                totalFinalPrice,
                totalSavings,
                activeTier: activeTiers.length > 0 ? { ...activeTiers[0], totalAccumulatedPercentage: exactDiscountPercentage } : null,
                nextTier,
                missingForNext,
                handleApply,
                applying,
                applied,
                applyError
            } : null);
        }
    }, [
        productPromos.length, groupProducts.length, totalQty, totalRawPrice, totalFinalPrice,
        totalSavings, activeTiers, nextTier, missingForNext, applying, applied, applyError,
        handleApply, onActiveItemsChange, activeDiscountPercentageResult
    ]);

    if (productPromos.length === 0 || groupProducts.length === 0) {
        return null;
    }

    return (
        <div className="ofertas-combinables border rounded p-3 mb-3 bg-white">
            <h6 className="text-primary mb-2">🔗 Ofertas combinables</h6>

            {/* Tier descriptions */}
            {productPromos.map(tier => (
                <p key={tier.id} className="mb-1 small"
                    style={{ color: totalQty >= (tier.minQuantity ?? 0) ? '#198754' : '#6c757d' }}>
                    {totalQty >= (tier.minQuantity ?? 0) ? '✅' : '○'}
                    {' '}A elegir <strong>{tier.minQuantity} uds</strong> para un{' '}
                    <strong>{tier.discountPercentage}% de descuento</strong>
                </p>
            ))}

            <hr className="my-2" />

            {/* Product rows */}
            <div className="d-flex flex-column gap-2 mb-3">
                {groupProducts.map(p => {
                    const qty = quantities[p.codigoArticulo] ?? 0;
                    const outOfStock = p.stock != null && p.stock <= 0;
                    const basePrice = getProductPrice(p);
                    const originalBasePrice = getProductOriginalPrice(p) || basePrice;
                    const discount = activeDiscountPercentageResult;
                    // OVERRIDE: apply % on catalog price (bypasses ERP discounts)
                    // Others: apply % on ERP selling price
                    const finalPrice = isOverrideStacking
                        ? originalBasePrice * (1 - (discount / 100))
                        : basePrice * (1 - (discount / 100));

                    // Calculate true combined discount percentage compared to catalog base price
                    const exactDiscountPercentage = originalBasePrice > 0
                        ? (1 - (finalPrice / originalBasePrice)) * 100
                        : 0;
                    const hasDiscount = exactDiscountPercentage > 0;

                    return (
                        <div key={p.codigoArticulo}
                            className={`d-flex align-items-center gap-2 p-2 border rounded${outOfStock ? ' opacity-50' : ''}`}>
                            {p.imagenUrl && (
                                <img src={p.imagenUrl} alt={p.nombreArticulo}
                                    style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                            )}
                            <div className="flex-grow-1 small">
                                <div className="fw-bold">{p.nombreArticulo}</div>
                                <div className="text-muted d-flex align-items-center flex-wrap gap-2" style={{ fontSize: '0.75rem' }}>
                                    <span>{p.codigoArticulo}</span>
                                    {originalBasePrice > 0 && (
                                        <div className="ms-md-2" style={{ fontSize: '0.8rem' }}>
                                            {hasDiscount ? (
                                                <>
                                                    <span className="text-decoration-line-through text-muted">{originalBasePrice.toFixed(2).replace('.', ',')} €</span>
                                                    {' '}
                                                    <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>{finalPrice.toFixed(2).replace('.', ',')} €</span>
                                                    {' '}
                                                    <span className={discount > 0 ? "badge bg-success" : "badge bg-primary"}>
                                                        -{exactDiscountPercentage.toFixed(0)}%
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="fw-bold">{originalBasePrice.toFixed(2).replace('.', ',')} €</span>
                                            )}
                                            <span className="text-muted ms-1" style={{ fontSize: '0.65rem' }}>/ ud</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <button className="btn btn-outline-secondary btn-sm px-2 py-0"
                                    onClick={() => handleQtyChange(p.codigoArticulo, -1)}
                                    disabled={qty === 0 || outOfStock}>−</button>
                                <span className="px-2 fw-bold" style={{ minWidth: 24, textAlign: 'center' }}>{qty}</span>
                                <button className="btn btn-outline-secondary btn-sm px-2 py-0"
                                    onClick={() => handleQtyChange(p.codigoArticulo, 1)}
                                    disabled={outOfStock || (p.stock != null && qty >= p.stock)}>+</button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {applyError && (
                <div className="alert alert-danger mt-2 py-1 small">{applyError}</div>
            )}
        </div>
    );
};
